import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publishedArticles, publishedBySlug, formatArticleDate } from '../data/articles';

const BlogArticle: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? publishedBySlug[slug] : null;

  useEffect(() => {
    if (!article) return;

    const previousTitle = document.title;
    document.title = `${article.title} | RentSparks`;

    let metaDescription = document.querySelector('meta[name="description"]');
    const previousDescription = metaDescription?.getAttribute('content') ?? null;
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', article.excerpt);

    return () => {
      document.title = previousTitle;
      if (previousDescription !== null) {
        metaDescription?.setAttribute('content', previousDescription);
      }
    };
  }, [article]);

  if (!article) {
    return (
      <div className="container mx-auto px-4 py-20">
        <h1 className="text-2xl font-bold">Article not found</h1>
      </div>
    );
  }

  // Get related articles (excluding current article)
  const relatedArticles = publishedArticles
    .filter((a) => a.slug !== slug)
    .filter((a) => a.tags.some(tag => article.tags.includes(tag)))
    .slice(0, 3);

  return (
    <article className="py-20 bg-[#FAFAF5]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-[400px] object-cover rounded-lg mb-8"
          />

          <div className="mb-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {article.tags.map(tag => (
                <span
                  key={tag}
                  className="text-sm bg-light-gray px-3 py-1 rounded-full text-dark-gray"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-4xl md:text-5xl font-abadi font-medium mb-4 leading-tight">{article.title}</h1>
            <div className="text-dark-gray text-lg">
              <div>{formatArticleDate(article.publishDate)}</div>
            </div>
          </div>

          <div
            className="prose prose-xl max-w-none mb-12 text-lg leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {relatedArticles.length > 0 && (
            <div className="border-t border-gray-200 pt-8 mt-12">
              <h2 className="text-2xl font-abadi font-medium mb-6">You might also be interested in reading</h2>
              <div className="space-y-4">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/blog/${related.slug}`}
                    className="block bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                  >
                    <h3 className="font-medium text-xl mb-2">{related.title}</h3>
                    <div className="flex flex-wrap gap-2">
                      {related.tags.map(tag => (
                        <span
                          key={tag}
                          className="text-xs bg-light-gray px-2 py-1 rounded-full text-dark-gray"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default BlogArticle;
